import { loadWhenVisible } from '@theme/video-lazyload';

/**
 * Wraps an autoplay background `<video>` whose `<source>` URLs are held in
 * `data-src` so the browser doesn't download them on page load. The video is
 * loaded and played only once it nears the viewport — or immediately when the
 * host carries a `preload` attribute (for an above-the-fold hero).
 *
 * This is the counterpart to <video-background-component> for videos emitted
 * directly by Liquid's `video_tag` filter (hero, slideshow slides, lander,
 * the video block), where the data-driven `<source>` ref pattern isn't
 * available.
 */
class LazyAutoplayVideo extends HTMLElement {
  /** @type {(() => void) | undefined} */
  #stopObserving;
  #loaded = false;

  connectedCallback() {
    if (this.hasAttribute('preload')) {
      this.#load();
      return;
    }

    // Observe the <video>, not the host: the host is `display: contents`, so an
    // absolutely-positioned video (the hero) leaves it collapsed to 0×0 and it
    // never intersects. The <video> always has a real box.
    const target = this.querySelector('video') ?? this;
    this.#stopObserving = loadWhenVisible(target, () => this.#load());
  }

  disconnectedCallback() {
    this.#stopObserving?.();
  }

  #load() {
    if (this.#loaded) return;
    this.#loaded = true;

    const video = this.querySelector('video');
    if (!video) return;

    let changed = false;
    for (const source of video.querySelectorAll('source[data-src]')) {
      source.setAttribute('src', /** @type {HTMLElement} */ (source).dataset.src ?? '');
      source.removeAttribute('data-src');
      changed = true;
    }

    if (changed) video.load();

    // `autoplay` alone doesn't reliably start playback when the source is
    // attached after the initial load, so kick it off explicitly. The video is
    // muted + playsinline, so this is allowed under autoplay policies. Ignore
    // the rejection that occurs if the element is removed before play starts.
    video.play()?.catch(() => {});
  }
}

if (!customElements.get('lazy-autoplay-video')) {
  customElements.define('lazy-autoplay-video', LazyAutoplayVideo);
}
